import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '600px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#FFF',
  },
  markAllReadButton: {
    backgroundColor: '#1F2937',
    color: '#3B82F6',
    border: '1px solid #374151',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  markAllReadButtonHover: {
    backgroundColor: '#374151',
  },
  notificationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  notificationItem: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    gap: '12px',
  },
  notificationItemUnread: {
    borderColor: '#3B82F6',
    backgroundColor: '#0f172a',
  },
  notificationIcon: {
    fontSize: '24px',
    minWidth: '32px',
    textAlign: 'center',
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  notificationMessage: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  notificationTime: {
    fontSize: '12px',
    color: '#6B7280',
  },
  unreadBadge: {
    width: '8px',
    height: '8px',
    backgroundColor: '#3B82F6',
    borderRadius: '50%',
    minWidth: '8px',
    minHeight: '8px',
    marginTop: '4px',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9CA3AF',
  },
};

const notificationIcons = {
  alert: '⚠️',
  info: 'ℹ️',
  success: '✓',
  warning: '⚡',
  announcement: '📢',
  ticket: '🎫',
  invoice: '📄',
  contract: '📋',
  onboarding: '🚀',
};

export default function ClientNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/notifications');
        setNotifications(Array.isArray(data) ? data : data.notifications || []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load notifications');
        console.error('Notifications error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );

      await clientFetch(`/client/portal/notifications/${notificationId}/read`, {
        method: 'PUT',
      }).catch(() => {
        // Revert on error
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId ? { ...notif, read: false } : notif
          )
        );
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    const unreadNotifs = notifications.filter((n) => !n.read);
    if (unreadNotifs.length === 0) return;

    try {
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, read: true }))
      );

      await clientFetch('/client/portal/notifications/mark-all-read', {
        method: 'PUT',
      }).catch(() => {
        // Revert on error
        setNotifications((prev) =>
          prev.map((notif, idx) => ({
            ...notif,
            read: !unreadNotifs.find((u) => u.id === notif.id),
          }))
        );
      });
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotificationIcon = (type) => {
    return notificationIcons[type] || '📬';
  };

  if (loading) {
    return (
      <ClientShell title="Notifications">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading notifications...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Notifications">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <ClientShell title="Notifications">
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Notifications</div>
          {unreadCount > 0 && (
            <button
              style={styles.markAllReadButton}
              onClick={handleMarkAllRead}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1F2937';
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        {notifications && notifications.length > 0 ? (
          <div style={styles.notificationsList}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  ...styles.notificationItem,
                  ...(notification.read ? {} : styles.notificationItemUnread),
                }}
                onClick={() => handleMarkAsRead(notification.id)}
                onMouseEnter={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.borderColor = '#1F2937';
                    e.currentTarget.style.backgroundColor = '#111827';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.borderColor = '#3B82F6';
                    e.currentTarget.style.backgroundColor = '#0f172a';
                  }
                }}
              >
                <div style={styles.notificationIcon}>
                  {getNotificationIcon(notification.type)}
                </div>
                <div style={styles.notificationContent}>
                  <div style={styles.notificationTitle}>
                    {notification.title}
                  </div>
                  <div style={styles.notificationMessage}>
                    {notification.message}
                  </div>
                  <div style={styles.notificationTime}>
                    {notification.time}
                  </div>
                </div>
                {!notification.read && <div style={styles.unreadBadge} />}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>No notifications</p>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
